import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';

/** A direct-sale invoice is a SalesOrder with documentType 'direct_sale' (db-sales.js) —
 *  typed up with no quotation/proforma behind it, e.g. a walk-in/cash sale. */
export interface InvoiceLine {
  productId?: string | null;
  description?: string;
  hsnSac?: string;
  unit?: string;
  packSize?: string;
  qtyPerPack?: number;
  totalPackSize?: string;
  volumeLiters?: number;
  quantity: number;
  unitPrice: number;
  discount?: number;
  discountType?: 'fixed' | 'percent';
  cgstRate?: number;
  sgstRate?: number;
  igstRate?: number;
  lineTotal?: number;
}

export interface DispatchStatusInfo {
  stage: 'awaiting_dispatch' | 'draft_challan' | 'dispatched' | 'delivered' | 'not_handed_off' | string;
  label: string;
  challanNumber?: string;
  dispatchedAt?: string | null;
  deliveredAt?: string | null;
  courierName?: string;
  lrNumber?: string;
}

export interface InvoiceDraft {
  _id: string;
  documentNumber?: string;
  orderNumber?: string;
  date: string;
  documentType: string;
  status: string;
  customerId?: { _id: string; name: string; email?: string; phone?: string } | string | null;
  customerName?: string;
  customerEmail?: string;
  customerPhone?: string;
  lines?: InvoiceLine[];
  subTotal?: number;
  taxTotal?: number;
  grandTotal?: number;
  notes?: string;
  createdBy?: { display_name?: string } | string | null;
  dispatchStatus?: DispatchStatusInfo;
}

const KEY = ['invoices-direct'];

/** Shared across both invoice sources — the same allowlisted keys ORDER_SORTS
 *  (src/api-sales/orders.js) and INVOICE_REQUEST_SORTS (src/api-invoice-requests)
 *  both accept, so InvoicesPage can sort its merged list by one chosen field. */
export type InvoiceSort = 'date_desc' | 'date_asc' | 'amount_desc' | 'amount_asc' | 'customer_asc' | 'customer_desc' | 'invoice_desc' | 'invoice_asc';

export interface InvoiceListParams {
  status?: string;
  search?: string;
  page?: number;
  limit?: number;
  sort?: InvoiceSort;
  createdBy?: string;
  /** A catalog product's _id — matches lines.productId server-side (same
   *  GET /api/sales-ops/orders filter the Quotations list uses). Only
   *  narrows the direct-sale rows this hook returns, not Tax Invoice rows
   *  (a separate source — see useTaxInvoiceList). */
  productId?: string;
  /** Date range, YYYY-MM-DD, whole Indian days. */
  fromDate?: string;
  toDate?: string;
}

export interface PagedResult<T> {
  data: T[];
  pagination: { total: number; page: number; limit: number; totalPages: number };
  stats?: { totalValue: number; openCount?: number };
}

export function useInvoiceList(params: InvoiceListParams | string = {}) {
  const { status, search, page = 1, limit = 50, sort, createdBy, productId, fromDate, toDate } = typeof params === 'string' ? { status: params } as InvoiceListParams : params;
  return useQuery({
    queryKey: [...KEY, 'list', { status: status || 'all', search, page, limit, sort, createdBy, productId, fromDate, toDate }],
    queryFn: async () => {
      const p = new URLSearchParams({
        documentType: 'direct_sale',
        compact: 'true',
        page: String(page),
        limit: String(limit),
      });
      if (status) p.set('status', status);
      if (search?.trim()) p.set('search', search.trim());
      if (sort) p.set('sort', sort);
      if (createdBy) p.set('createdBy', createdBy);
      if (productId) p.set('productId', productId);
      if (fromDate) p.set('fromDate', fromDate);
      if (toDate) p.set('toDate', toDate);
      const { data } = await api.get<PagedResult<InvoiceDraft>>(`/api/sales-ops/orders?${p}`);
      return data;
    },
    placeholderData: keepPreviousData,
  });
}

/** A real, ledger-posted Tax Invoice — generated from a Quotation/Proforma/direct
 *  sale via "Create Tax Invoice" (src/api-invoice-requests). The invoice_requests
 *  list is used as the read model here (rather than /api/accounts/sales-invoices)
 *  because it is already scoped the way this page needs: a Sales user sees only
 *  their own, Accounts/admin/superadmin see all — no accounts-department gate. */
export interface TaxInvoiceLine {
  name: string;
  description?: string;
  quantity: number;
  rate: number;
  taxRate?: number;
}

export interface TaxInvoiceRow {
  _id: string;
  number: string;
  invoiceId: string;
  invoiceNumber: string;
  clientName: string;
  items: TaxInvoiceLine[];
  subtotal: number;
  taxTotal: number;
  total: number;
  requestedAt: string;
  invoiceDate?: string;
  date?: string;
  sourceQuotationNumber?: string;
  status: string;
  /** true once the invoice has a GST number and is posted to the ledger. */
  finalized: boolean;
  /** The invoice's own status — 'cancelled' / 'voided' / 'reversed' once withdrawn. */
  invoiceStatus?: string;
  /** A cancelled invoice: its amount before it was zeroed, and why. */
  originalTotal?: number;
  cancelledAt?: string | null;
  cancellationReason?: string;
  deliveryChallan?: { _id: string; documentNumber: string; status: string } | null;
  needsApproval?: boolean;
  /** The 'sales_order' this invoice was raised from — lets the Invoices list
   * trigger "Hand off to Dispatch" directly, targeting the same record the
   * Sales Orders screen's own hand-off action already uses. */
  salesOrderId?: string | null;
  dispatchStatus?: DispatchStatusInfo;
}

export interface TaxInvoiceListParams {
  search?: string;
  page?: number;
  limit?: number;
  sort?: InvoiceSort;
  createdBy?: string;
  fromDate?: string;
  toDate?: string;
  /** Exact client name (case-insensitive) — the Client filter. */
  client?: string;
  /** Catalog product on any line — the Product filter. */
  productId?: string;
}

/** Clients that have invoices — options for the Invoices page's Client filter. */
export interface InvoiceProduct { id: string; name: string; sku: string; hsnSac: string; retired: boolean; invoices: number }

/** Products that appear on invoices — options for the Invoices page's Product
 *  filter (every one, including retired catalogue items old invoices carry). */
export function useInvoiceProducts() {
  return useQuery({
    queryKey: ['invoice-requests', 'products'],
    queryFn: async () => (await api.get<{ products: InvoiceProduct[] }>('/api/invoice-requests/products')).data.products,
    staleTime: 60_000,
  });
}

export function useInvoiceClients() {
  return useQuery({
    queryKey: ['invoice-requests', 'clients'],
    queryFn: async () => (await api.get<{ clients: string[] }>('/api/invoice-requests/clients')).data.clients,
    staleTime: 60_000,
  });
}

const CANCELLED_INVOICE = ['cancelled', 'voided', 'reversed'];

export function useTaxInvoiceList(params: TaxInvoiceListParams = {}) {
  const { search, page = 1, limit = 50, sort, createdBy, fromDate, toDate, client, productId } = params;
  return useQuery({
    queryKey: ['invoice-requests', 'invoiced', { search, page, limit, sort, createdBy, fromDate, toDate, client, productId }],
    queryFn: async () => {
      // Both 'approved' (draft invoice created, not posted) and 'sent'
      // (finalised) — the Invoices list shows the draft with a Finalize action.
      const params = new URLSearchParams({ page: String(page), limit: String(limit) });
      if (search?.trim()) params.set('search', search.trim());
      if (sort) params.set('sort', sort);
      if (createdBy) params.set('requestedBy', createdBy);
      if (fromDate) params.set('fromDate', fromDate);
      if (toDate) params.set('toDate', toDate);
      if (client) params.set('client', client);
      if (productId) params.set('productId', productId);
      const { data } = await api.get<{
        requests: (TaxInvoiceRow & { invoiceId?: string })[];
        pagination?: { total: number; page: number; limit: number; totalPages: number };
        stats?: { totalValue: number; billCount?: number; firstBillNo?: string; lastBillNo?: string };
      }>(
        `/api/invoice-requests?${params}`,
      );
      const rows = (data.requests ?? [])
        // A cancelled tax invoice stays listed (amount ₹0, no actions) — only a
        // request that never became an invoice is left out.
        .filter((r) => r.invoiceId && (CANCELLED_INVOICE.includes(r.invoiceStatus || '') || (r.status !== 'cancelled' && r.status !== 'discarded' && r.status !== 'rejected')))
        .map((r) => ({ ...r, finalized: r.status === 'sent' || CANCELLED_INVOICE.includes(r.invoiceStatus || '') }));
      return {
        data: rows,
        pagination: data.pagination ?? { total: rows.length, page, limit, totalPages: 1 },
        stats: data.stats,
      };
    },
    placeholderData: keepPreviousData,
  });
}

export interface CreateInvoicePayload {
  customerName: string;
  customerEmail?: string;
  customerPhone?: string;
  notes?: string;
  lines: InvoiceLine[];
}

export interface UpdateTaxInvoicePayload {
  notes?: string;
  lineItems: Array<{
    productId?: string | null;
    description: string;
    hsnSac?: string;
    unit?: string;
    packSize?: string;
    qtyPerPack?: number;
    totalPackSize?: string;
    volumeLiters?: number;
    quantity: number;
    rate: number;
    discount?: number;
    cgstRate?: number;
    sgstRate?: number;
    igstRate?: number;
  }>;
}

export function useInvoiceMutations() {
  const qc = useQueryClient();
  const invalidate = () => Promise.all([
    qc.invalidateQueries({ queryKey: KEY }),
    qc.invalidateQueries({ queryKey: ['invoice-requests'] }),
  ]);

  const create = useMutation({
    mutationFn: async (payload: CreateInvoicePayload) => {
      const { data } = await api.post('/api/sales-ops/orders', {
        ...payload,
        documentType: 'direct_sale',
        date: new Date().toISOString(),
      });
      return data as InvoiceDraft;
    },
    onSuccess: invalidate,
  });

  const update = useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: CreateInvoicePayload }) => {
      const { data } = await api.put(`/api/sales-ops/orders/${id}`, payload);
      return data as InvoiceDraft;
    },
    onSuccess: invalidate,
  });

  const updateTaxInvoice = useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: UpdateTaxInvoicePayload }) => {
      const { data } = await api.put(`/api/accounts/sales-invoices/${id}`, payload);
      return data;
    },
    onSuccess: invalidate,
  });

  const createDeliveryChallan = useMutation({
    mutationFn: async (invoiceId: string) => {
      const { data } = await api.post(`/api/accounts/sales-invoices/${invoiceId}/delivery-challan`);
      return data;
    },
    onSuccess: invalidate,
  });

  const discardDraftInvoice = useMutation({
    // POST /:id/discard has payment-count guard, InvoiceRequest cleanup and
    // audit-log. The bare DELETE /:id only checks status === 'draft' and
    // hard-deletes with no cleanup, so it must NOT be used for Discard.
    mutationFn: async (invoiceId: string) => {
      const { data } = await api.post(`/api/accounts/sales-invoices/${invoiceId}/discard`);
      return data;
    },
    onSuccess: invalidate,
  });

  const discardDirectSaleDraft = useMutation({
    mutationFn: async (orderId: string) => {
      const { data } = await api.post(`/api/sales-ops/orders/${orderId}/discard-draft`, { reason: 'Draft discarded by user' });
      return data;
    },
    onSuccess: invalidate,
  });

  return { create, update, updateTaxInvoice, createDeliveryChallan, discardDraftInvoice, discardDirectSaleDraft };
}
